import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { del, get, post } from "../../lib/request"
import { getWorldCityId } from "../../lib/worldCity"

function formatDateToLocalYmd(value) {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim()
  }
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return String(value ?? "")
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export function eventsHolidaysCacheKey({ clientId, date, year, month, startDate, endDate } = {}) {
  if (startDate && endDate) {
    return `${String(clientId ?? "")}|${formatDateToLocalYmd(startDate)}|${formatDateToLocalYmd(endDate)}`
  }
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return `${String(clientId ?? "")}|${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
  }
  if (year != null && month != null && month !== "") {
    const m = Number(month)
    const monthIndex = m >= 1 && m <= 12 ? m - 1 : m
    return `${String(clientId ?? "")}|${Number(year)}-${String(monthIndex + 1).padStart(2, "0")}`
  }
  const now = new Date()
  const y = year != null ? Number(year) : now.getFullYear()
  return `${String(clientId ?? "")}|${y}-${String(now.getMonth() + 1).padStart(2, "0")}`
}

function flattenRowsForClient(byMonth, clientId) {
  const prefix = `${String(clientId)}|`
  const seen = new Set()
  const out = []
  Object.entries(byMonth || {}).forEach(([key, rows]) => {
    if (!key.startsWith(prefix) || !Array.isArray(rows)) return
    rows.forEach((row) => {
      const id = row?.id ?? row?.eventId
      if (id != null) {
        const sid = String(id)
        if (seen.has(sid)) return
        seen.add(sid)
      }
      out.push(row)
    })
  })
  return out
}

/** Query range for events/holidays APIs: explicit dates, else the given month. */
export function eventHolidayMonthQueryRange({ date, year, month, startDate, endDate } = {}) {
  if (startDate && endDate) {
    return {
      start_date: formatDateToLocalYmd(startDate),
      end_date: formatDateToLocalYmd(endDate),
    }
  }

  let y
  let monthIndex
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    y = date.getFullYear()
    monthIndex = date.getMonth()
  } else if (year != null && month != null && month !== "") {
    y = Number(year)
    const m = Number(month)
    monthIndex = m >= 1 && m <= 12 ? m - 1 : m
  } else {
    const now = new Date()
    y = year != null ? Number(year) : now.getFullYear()
    monthIndex = now.getMonth()
  }

  const start = new Date(y, monthIndex, 1)
  const end = new Date(y, monthIndex + 1, 0)
  return {
    start_date: formatDateToLocalYmd(start),
    end_date: formatDateToLocalYmd(end),
  }
}

// Async thunk for fetching events by client ID
export const fetchEventsByClientId = createAsyncThunk(
  "events/fetchByClientId",
  async ({ clientId, date, year, month, startDate, endDate, force = false }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!clientId) {
        throw new Error("Client ID is required")
      }

      const cacheKey = eventsHolidaysCacheKey({ clientId, date, year, month, startDate, endDate })
      if (!force && state.events.fetchedMonths?.[cacheKey]) {
        return {
          data: state.events.eventsByMonth?.[cacheKey] || [],
          fromCache: true,
          cacheKey,
          clientId,
        }
      }

      const { start_date, end_date } = eventHolidayMonthQueryRange({ date, year, month, startDate, endDate })

      const queryParams = new URLSearchParams({
        start_date,
        end_date,
      }).toString()

      const data = await get(`events/clients/${clientId}?${queryParams}`)

      return { data, fromCache: false, cacheKey, clientId }
    } catch (err) {
      console.error(" Events fetch error:", err)
      return rejectWithValue(err?.message || "Failed to fetch events")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      if (!arg?.clientId) return false
      const key = eventsHolidaysCacheKey(arg)
      const slice = getState().events ?? {}
      if (slice.fetchedMonths?.[key]) return false
      if (slice.inFlightMonths?.[key]) return false
      return true
    },
  },
)

export const deleteEventForClient = createAsyncThunk(
  "events/deleteForClient",
  async ({ eventId }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (!state.auth?.token) throw new Error("Authentication required")
      if (eventId == null || eventId === "") {
        throw new Error("Event is required")
      }
      await del(`events/${encodeURIComponent(String(eventId))}`)
      return { eventId }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete event")
    }
  },
)

export const createClientEvent = createAsyncThunk(
  "events/createClientEvent",
  async ({ clientId, eventData }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (!state.auth?.token) throw new Error("Authentication required")
      if (!clientId) throw new Error("Client ID is required")
      if (!eventData?.name && !eventData?.title) throw new Error("Event title is required")
      if (!eventData?.date && !eventData?.startDate) throw new Error("Event date is required")

      const clientsState = state.clients || {}
      const selectedClient = clientsState.selectedClient || null
      const fromSelectedClient =
        selectedClient &&
        String(selectedClient.id ?? selectedClient.clientId) === String(clientId)
          ? getWorldCityId(selectedClient)
          : null
      const matchedClientRow = Array.isArray(clientsState.clients)
        ? clientsState.clients.find((c) => String(c?.id ?? c?.clientId) === String(clientId))
        : null
      const fromClientsList = getWorldCityId(matchedClientRow)
      const resolvedCityId =
        eventData?.cityId ??
        fromSelectedClient ??
        fromClientsList ??
        null
      const rawDate = eventData?.date || eventData?.startDate
      const normalizedDate = formatDateToLocalYmd(rawDate)
      const resolvedCityIdNum =
        resolvedCityId == null || String(resolvedCityId).trim() === ""
          ? null
          : Number(resolvedCityId)

      const payload = {
        name: String(eventData.name || eventData.title).trim(),
        description: String(eventData.description || "").trim(),
        date: normalizedDate,
        addToCalendar:
          eventData.addToCalendar === undefined ? true : Boolean(eventData.addToCalendar),
        ...(resolvedCityIdNum != null && !Number.isNaN(resolvedCityIdNum)
          ? {
              worldCity: { id: resolvedCityIdNum },
            }
          : {}),
      }

      const data = await post(`events/clients/${clientId}`, payload)
      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create event")
    }
  },
)

const initialState = {
  events: [],
  status: "idle", // idle | loading | succeeded | failed
  error: null,
  eventsByMonth: {},
  fetchedMonths: {},
  inFlightMonths: {},
}

const eventsSlice = createSlice({
  name: "events",
  initialState,
  reducers: {
    clearEvents: (state) => {
      state.events = []
      state.status = "idle"
      state.error = null
      state.eventsByMonth = {}
      state.fetchedMonths = {}
      state.inFlightMonths = {}
    },
    hydrateEventsForClient: (state, action) => {
      state.events = flattenRowsForClient(state.eventsByMonth, action.payload)
    },
    clearEventsError: (state) => {
      state.error = null
    },
    resetEventsOnLogout: (state) => {
      state.events = []
      state.status = "idle"
      state.error = null
      state.eventsByMonth = {}
      state.fetchedMonths = {}
      state.inFlightMonths = {}
    },
    /**
     * After POST /events/clients/{id}, GET may omit planner-only rows (e.g. addToCalendar: false).
     * Keep the created row in state so AI Planner / lists still show it; calendar sync stays backend-owned.
     */
    mergeCreatedClientEvent: (state, action) => {
      const raw = action.payload
      if (!raw || typeof raw !== "object") return
      const inner =
        raw.data && typeof raw.data === "object" && !Array.isArray(raw.data) ? raw.data : raw
      const id = inner.id ?? inner.eventId ?? raw.id ?? raw.eventId
      if (id == null) return
      const list = Array.isArray(state.events) ? state.events : []
      if (list.some((e) => String(e?.id ?? e?.eventId) === String(id))) return
      const row = { ...inner }
      if (row.id == null && row.eventId == null) row.id = id
      state.events = [...list, row]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEventsByClientId.pending, (state, action) => {
        const key = eventsHolidaysCacheKey(action.meta?.arg || {})
        if (!state.inFlightMonths) state.inFlightMonths = {}
        if (key) state.inFlightMonths[key] = true
        if (action.meta?.arg?.force) {
          state.status = "loading"
          state.error = null
          return
        }
        if (state.fetchedMonths?.[key]) return
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchEventsByClientId.fulfilled, (state, action) => {
        state.status = "succeeded"
        const payload = action.payload
        const fromCache = payload?.fromCache
        const cacheKey = payload?.cacheKey
        const clientId = payload?.clientId
        const data = fromCache || cacheKey ? payload?.data : payload
        if (cacheKey && state.inFlightMonths) delete state.inFlightMonths[cacheKey]
        if (!fromCache && cacheKey && Array.isArray(data)) {
          if (!state.eventsByMonth) state.eventsByMonth = {}
          if (!state.fetchedMonths) state.fetchedMonths = {}
          state.eventsByMonth[cacheKey] = data
          state.fetchedMonths[cacheKey] = true
        }
        state.events = flattenRowsForClient(state.eventsByMonth, clientId)
      })
      .addCase(fetchEventsByClientId.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to fetch events"
        const key = eventsHolidaysCacheKey(action.meta?.arg || {})
        if (key && state.inFlightMonths) delete state.inFlightMonths[key]
      })

      .addCase(deleteEventForClient.pending, (state) => {
        state.error = null
      })
      .addCase(deleteEventForClient.fulfilled, (state, action) => {
        const removed = String(action.payload?.eventId ?? "")
        if (!removed) return
        state.events = (state.events || []).filter((e) => {
          const id = e?.id != null ? String(e.id) : ""
          const eid = e?.eventId != null ? String(e.eventId) : ""
          return id !== removed && eid !== removed
        })
      })
      .addCase(deleteEventForClient.rejected, (state, action) => {
        state.error = action.payload || "Failed to delete event"
      })

      .addCase(createClientEvent.pending, (state) => {
        state.error = null
      })
      .addCase(createClientEvent.fulfilled, (state, action) => {
        eventsSlice.caseReducers.mergeCreatedClientEvent(state, action)
      })
      .addCase(createClientEvent.rejected, (state, action) => {
        state.error = action.payload || "Failed to create event"
      })
  },
})

export const {
  clearEvents,
  clearEventsError,
  resetEventsOnLogout,
  mergeCreatedClientEvent,
  hydrateEventsForClient,
} = eventsSlice.actions

export default eventsSlice.reducer
